import { type TemplateResult, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { type QuestionaireData } from '../../interfaces/interfaces.js';
import { TailwindElement } from '../../shared/tailwind.element.js';
import { questionaireContext } from '../../context/context.js';
import { CustomEventNames } from '../../constants/custom-event-names.js';
import { textItems } from '../../constants/text.js';

@customElement('szw-ba-dialog')
export class SzwBaDialog extends TailwindElement() {
	@consume({ context: questionaireContext, subscribe: true })
	@property({ attribute: false })
	data!: QuestionaireData;

	@query('#dialog')
	private readonly dialog!: HTMLDialogElement;

	private readonly boundHandleClose: (event: Event) => void;
	private readonly boundHandleResumeLater: (event: Event) => void;

	constructor() {
		super();
		this.boundHandleClose = this.handleClose.bind(this);
		this.boundHandleResumeLater = this.handleResumeLater.bind(this);
	}

	connectedCallback(): void {
		super.connectedCallback();
		document.addEventListener(CustomEventNames.ResumeLater, this.boundHandleResumeLater);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		document.removeEventListener(CustomEventNames.ResumeLater, this.boundHandleResumeLater);
	}

	handleResumeLater(): void {
		this.dialog.showModal();
	}

	handleClose(event: Event): void {
		if (event instanceof KeyboardEvent && event.code !== 'Enter' && event.code !== 'Escape' && event.code !== 'Space') {
			return;
		}
		this.dialog.close();
	}

	render(): TemplateResult {
		return html`
			<dialog aria-modal="true" id="dialog" aria-labelledby="dialogheader" class="dialog w-full max-w-xl">
				<section class="flex flex-col gap-6 bg-white p-6">
					<header class="flex justify-between gap-2">
						<h2 class="dialogheader">${textItems.resumeLater}</h2>
						<button type="button" @click=${this.boundHandleClose} aria-label="${textItems.closeAndContinue}">
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								class="shrink-0"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								title="${textItems.icons.close}"
							>
								<g>
									<path
										d="M17.6871 4.21371L12.0103 9.24151L6.33349 4.21361C5.73649 3.68481 4.83499 3.69821 4.25439 4.24411C3.70839 4.82461 3.69499 5.72621 4.22389 6.32311L9.25179 12L4.22389 17.6769C3.69499 18.2738 3.70839 19.1754 4.25429 19.7559C4.83489 20.3018 5.73639 20.3152 6.33339 19.7863L12.0102 14.7584L17.687 19.7863C18.2839 20.3152 19.1855 20.3018 19.7661 19.756C20.312 19.1754 20.3254 18.2739 19.7965 17.6769L14.7687 12L19.7966 6.32321C20.3255 5.72621 20.312 4.82471 19.7662 4.24411C19.1855 3.69821 18.284 3.68481 17.6871 4.21371ZM12.0103 12.0145L12.003 12.0072L11.9958 12L12.0031 11.9927L12.0103 11.9855L12.0176 11.9928L12.0248 12L12.0176 12.0072L12.0103 12.0145Z"
										fill="#696969"
									/>
								</g>
							</svg>
						</button>
					</header>
					<p>${textItems.resumeLaterClarification}</p>
					<p class="w-full bg-grijs-2 p-1 text-center text-3xl font-bold leading-normal text-grijs-6">
						${this.data?.responseCode}
					</p>
					<button
						class="btn btn--primary"
						type="button"
						@click=${this.boundHandleClose}
						aria-label="${textItems.closeAndContinue}"
					>
						${textItems.resume}
					</button>
				</section>
			</dialog>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'szw-ba-dialog': SzwBaDialog;
	}
}
